import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import api, { formatApiError } from "../lib/api";
import { formatMoney } from "../lib/money";
import { toast } from "sonner";
import {
  ShieldCheck, Lock, ChevronRight, AlertCircle, Info,
  Building2, Globe, IndianRupee, CheckCircle2,
} from "lucide-react";

/**
 * Sprint 4 — Checkout page.
 *
 * Steps:
 *   1. Show quote (server-side amount + tax)
 *   2. Collect billing details (Indian or international form)
 *   3. Show consent checkboxes + billing-entity notice
 *   4. Call /api/payments/checkout/start
 *      → INR: load Razorpay Checkout.js, open modal, post /verify/razorpay
 *      → Foreign: redirect to Stripe Checkout URL
 *
 * Server-side billing-eligibility engine has final say. The user's currency
 * toggle on the pricing page is cosmetic — if signals say "international",
 * the server flips currency on /checkout/start and tells us via the response.
 */
export default function CheckoutPage() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const planId = search.get("plan") || "starter_event";
  const initialCurrency = (search.get("currency") || "INR").toUpperCase();

  const [currency, setCurrency] = useState(initialCurrency);
  const [quote, setQuote] = useState(null);
  const [policies, setPolicies] = useState({});
  const [states, setStates] = useState([]);

  // Form state
  const [billing, setBilling] = useState({
    customer_type: "Individual", billing_name: "", organisation_name: "",
    billing_email: "", billing_phone: "",
    address_line_1: "", address_line_2: "", city: "",
    state: "", state_code: "", postal_code: "",
    country: currency === "INR" ? "India" : "",
    gstin: "", purchase_in_company_name: false,
    tax_id: "",
  });
  const [consents, setConsents] = useState({
    terms: false, privacy: false, refund: false, billing: false,
    confirm_billing: false, marketing: false, minor_consent: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [gstinWarning, setGstinWarning] = useState(null);

  // Load quote whenever plan / state / currency changes
  useEffect(() => {
    const params = {
      plan_id: planId,
      currency,
      indian_state_code: currency === "INR" ? billing.state_code : undefined,
    };
    api.post("/payments/checkout/quote", params)
      .then((r) => setQuote(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [planId, currency, billing.state_code]);

  useEffect(() => {
    api.get("/policies/").then((r) => {
      const map = {};
      for (const p of r.data.items) map[p.policy_key] = p;
      setPolicies(map);
    });
    api.get("/billing/indian-states").then((r) => setStates(r.data.items));
  }, []);

  // Live GSTIN check (state match)
  useEffect(() => {
    if (currency !== "INR" || !billing.gstin || !billing.state_code) {
      setGstinWarning(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(
          `/billing/gstin-state-check?gstin=${encodeURIComponent(billing.gstin)}&state_code=${billing.state_code}`,
        );
        if (!data.ok) setGstinWarning("GSTIN format looks invalid");
        else if (!data.match) setGstinWarning(data.warning);
        else setGstinWarning(null);
      } catch { setGstinWarning(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [billing.gstin, billing.state_code, currency]);

  const isInr = currency === "INR";
  const planIsFree = quote?.plan?.tier === "free" || quote?.total_payable === 0;

  const consentsOk = useMemo(() => {
    if (planIsFree) return true;
    return consents.terms && consents.privacy && consents.refund && consents.billing && consents.confirm_billing;
  }, [consents, planIsFree]);

  const submit = async () => {
    if (!consentsOk) {
      toast.warning("Please accept all required policies");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        plan_id: planId, currency,
        accept_terms: consents.terms,
        accept_privacy: consents.privacy,
        accept_refund_policy: consents.refund,
        accept_billing_notice: consents.billing,
        marketing_opt_in: consents.marketing,
        minor_consent: consents.minor_consent,
        success_url: window.location.origin + "/dashboard/billing/success",
        cancel_url: window.location.origin + "/dashboard/billing/cancel",
        indian_billing: isInr ? billing : undefined,
        intl_billing: !isInr ? billing : undefined,
        eligibility_signals: {
          billing_country: billing.country,
          phone_country_code: isInr ? "IN" : undefined,
          payment_method: isInr ? "card_in" : undefined,
        },
      };
      const { data } = await api.post("/payments/checkout/start", body);
      if (data.status === "manual_review") {
        toast.warning(data.professional_message ||
          "Billing verification required. Our team will reach out.");
        return;
      }
      if (data.free || data.status === "paid") {
        toast.success("Plan activated");
        navigate(`/dashboard/billing/success?payment_transaction_id=${data.payment_transaction_id}`);
        return;
      }
      // Server is the source of truth for currency + entity AFTER eligibility.
      // If the server flipped the locked currency (e.g. VPN, geo mismatch),
      // honour it instead of the cosmetic toggle from /pricing.
      const lockedCurrency = (data.currency || currency || "").toUpperCase();
      if (lockedCurrency && lockedCurrency !== currency) {
        setCurrency(lockedCurrency);
      }
      // Cross-entity safety: INR MUST route via Razorpay/Factskoot, and
      // any foreign currency MUST route via Stripe/Destineze. If somehow
      // we got a mismatch, bail rather than charging the user wrong.
      const isInrLocked = lockedCurrency === "INR";
      const expectedGateway = isInrLocked ? "razorpay" : "stripe";
      if (data.gateway && data.gateway !== expectedGateway) {
        toast.error("Billing region mismatch detected. Please refresh and try again.");
        return;
      }
      if (data.gateway === "stripe") {
        window.location.assign(data.checkout_url);
        return;
      }
      if (data.gateway === "razorpay") {
        if (data.mode === "mock") {
          // No live keys — simulate verify so the rest of the pipeline can be tested
          await api.post("/payments/verify/razorpay", {
            razorpay_order_id: data.razorpay_order_id,
            razorpay_payment_id: "pay_mock_" + Math.random().toString(36).slice(2, 14),
            razorpay_signature: "mock_signature_for_dev_only",
          });
          toast.success("Mock payment confirmed (Razorpay test keys not configured)");
          navigate(`/dashboard/billing/success?payment_transaction_id=${data.payment_transaction_id}`);
          return;
        }
        await loadRazorpayScript();
        const rzp = new window.Razorpay({
          key: data.razorpay_key_id,
          amount: data.amount_minor,
          currency: lockedCurrency || "INR",
          name: "Diplomexa",
          description: quote?.plan?.name,
          order_id: data.razorpay_order_id,
          handler: async (resp) => {
            try {
              await api.post("/payments/verify/razorpay", {
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              toast.success("Payment successful");
              navigate(`/dashboard/billing/success?payment_transaction_id=${data.payment_transaction_id}`);
            } catch (e) {
              toast.error("Payment verification failed");
              navigate(`/dashboard/billing/failed?payment_transaction_id=${data.payment_transaction_id}`);
            }
          },
          prefill: { name: billing.billing_name, email: billing.billing_email },
          theme: { color: "#0F4C81" },
          modal: { ondismiss: () => navigate(`/dashboard/billing`) },
        });
        rzp.open();
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed");
    } finally {
      setSubmitting(false);
    }
  };

  const fmt = (n) => formatMoney(n ?? 0, currency);

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto grid lg:grid-cols-12 gap-6" data-testid="checkout-root">
        {/* ----- left: order summary ----- */}
        <aside className="lg:col-span-5">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 sticky top-6" data-testid="checkout-summary-card">
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold flex items-center gap-1.5">
              <Lock className="h-3 w-3" /> Order summary
            </p>
            <h2 className="mt-1 text-xl font-bold text-[#0A192F]" data-testid="checkout-plan-name">
              {quote?.plan?.name || "Loading…"}
            </h2>
            <p className="text-xs text-slate-500">
              {quote?.plan?.billing_period === "annual" ? "Annual" : "One-time"} ·{" "}
              <span className={`${isInr ? "text-[#D4AF37]" : "text-[#0F4C81]"} font-semibold`}>
                {isInr ? "Billed by Factskoot" : "Billed by Destineze"}
              </span>
            </p>

            <div className="mt-5 space-y-2 text-sm">
              <Row label="Subtotal" value={fmt(quote?.subtotal)} />
              {(quote?.discount || 0) > 0 && (
                <Row label="Discount" value={`- ${fmt(quote.discount)}`} tone="success" />
              )}
              {isInr && quote?.tax?.gst_type === "CGST_SGST" && (
                <>
                  <Row label="CGST 9%" value={fmt(quote?.tax?.cgst)} />
                  <Row label="SGST 9%" value={fmt(quote?.tax?.sgst)} />
                </>
              )}
              {isInr && quote?.tax?.gst_type === "IGST" && (
                <Row label="IGST 18%" value={fmt(quote?.tax?.igst)} />
              )}
              {!isInr && (
                <Row label="UAE VAT" value="Not charged" tone="muted" />
              )}
              <div className="pt-2 mt-2 border-t border-slate-200 flex items-center justify-between text-base font-bold">
                <span>Total payable</span>
                <span data-testid="checkout-total">{fmt(quote?.total_payable)}</span>
              </div>
            </div>

            <div
              className={`mt-5 rounded-lg p-3 text-xs flex items-start gap-2 ${
                isInr ? "bg-[#D4AF37]/10 border border-[#D4AF37]/40 text-[#0A192F]"
                       : "bg-[#0F4C81]/5 border border-[#0F4C81]/30 text-[#0A192F]"
              }`}
              data-testid="checkout-billing-banner"
            >
              {isInr ? <IndianRupee className="h-4 w-4 mt-0.5" /> : <Globe className="h-4 w-4 mt-0.5" />}
              <span>
                {isInr ? (
                  <>Billed by <strong>Factskoot Eduwellness Private Limited</strong>, Bengaluru. 18% GST applicable.</>
                ) : (
                  <>Billed by <strong>Destineze Educational Voyages FZCO</strong>, UAE. UAE VAT not charged currently.</>
                )}
              </span>
            </div>

            <PolicyLinks />
          </div>
        </aside>

        {/* ----- right: form ----- */}
        <main className="lg:col-span-7 space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="checkout-billing-form">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#0F4C81] font-bold flex items-center gap-1.5">
              <Building2 className="h-3 w-3" /> Billing details
            </p>
            <h3 className="mt-1 text-lg font-semibold text-[#0A192F]">
              {isInr ? "India billing" : "International billing"}
            </h3>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              <Field label="Customer type" value={billing.customer_type}
                     onChange={(v) => setBilling({ ...billing, customer_type: v })}
                     as="select" options={["Individual", "Organisation", "School", "College", "Company", "NGO", "Government", "Other"]} />
              <Field label="Billing name *" value={billing.billing_name}
                     onChange={(v) => setBilling({ ...billing, billing_name: v })} />
              {billing.customer_type !== "Individual" && (
                <Field label="Organisation name" value={billing.organisation_name}
                       onChange={(v) => setBilling({ ...billing, organisation_name: v })} />
              )}
              <Field label="Email" value={billing.billing_email}
                     onChange={(v) => setBilling({ ...billing, billing_email: v })} />
              <Field label="Phone" value={billing.billing_phone}
                     onChange={(v) => setBilling({ ...billing, billing_phone: v })} />
              <Field label="Address line 1 *" value={billing.address_line_1} colSpan={2}
                     onChange={(v) => setBilling({ ...billing, address_line_1: v })} />
              <Field label="Address line 2" value={billing.address_line_2} colSpan={2}
                     onChange={(v) => setBilling({ ...billing, address_line_2: v })} />
              <Field label="City *" value={billing.city}
                     onChange={(v) => setBilling({ ...billing, city: v })} />
              {isInr ? (
                <Field
                  label="State *" value={billing.state_code}
                  onChange={(v) => {
                    const s = states.find((x) => x.code === v);
                    setBilling({ ...billing, state_code: v, state: s?.name || "" });
                  }}
                  as="select"
                  options={[{ code: "", name: "Select state" }, ...states].map((s) =>
                    s.code === "" ? { value: "", label: s.name } : { value: s.code, label: `${s.code} — ${s.name}` }
                  )}
                />
              ) : (
                <Field label="State / Region" value={billing.state}
                       onChange={(v) => setBilling({ ...billing, state: v })} />
              )}
              <Field label="Postal code" value={billing.postal_code}
                     onChange={(v) => setBilling({ ...billing, postal_code: v })} />
              {isInr ? (
                <>
                  <Field
                    label="GSTIN (optional)" value={billing.gstin}
                    onChange={(v) => setBilling({ ...billing, gstin: v.toUpperCase() })}
                    hint="Add GSTIN to receive a B2B invoice."
                  />
                  {gstinWarning && (
                    <div className="sm:col-span-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2 flex items-start gap-2" data-testid="gstin-warning">
                      <AlertCircle className="h-3.5 w-3.5 mt-0.5" />
                      {gstinWarning}
                    </div>
                  )}
                </>
              ) : (
                <Field label="Tax ID / VAT ID (optional)" value={billing.tax_id}
                       onChange={(v) => setBilling({ ...billing, tax_id: v })} />
              )}
              {!isInr && (
                <Field label="Country *" value={billing.country}
                       onChange={(v) => setBilling({ ...billing, country: v })} />
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="checkout-consents">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#0F4C81] font-bold">
              Consent & confirmation
            </p>
            <div className="mt-3 space-y-2 text-sm">
              <Check2 v={consents.terms} onChange={(v) => setConsents({ ...consents, terms: v })}
                      testid="consent-terms"
                      label={<>I accept the <Link to="/terms" target="_blank" className="text-[#0F4C81] underline">Terms and Conditions</Link></>} />
              <Check2 v={consents.privacy} onChange={(v) => setConsents({ ...consents, privacy: v })}
                      testid="consent-privacy"
                      label={<>I accept the <Link to="/privacy" target="_blank" className="text-[#0F4C81] underline">Privacy Policy</Link></>} />
              <Check2 v={consents.refund} onChange={(v) => setConsents({ ...consents, refund: v })}
                      testid="consent-refund"
                      label={<>I accept the <Link to="/refund-policy" target="_blank" className="text-[#0F4C81] underline">Refund & Cancellation Policy</Link></>} />
              <Check2 v={consents.billing} onChange={(v) => setConsents({ ...consents, billing: v })}
                      testid="consent-billing"
                      label={<>I have read the <Link to="/billing-notice" target="_blank" className="text-[#0F4C81] underline">Billing Entity Notice</Link></>} />
              <Check2 v={consents.confirm_billing} onChange={(v) => setConsents({ ...consents, confirm_billing: v })}
                      testid="consent-confirm-billing"
                      label="I confirm my billing details are correct for invoicing and tax purposes." />
              <Check2 v={consents.marketing} onChange={(v) => setConsents({ ...consents, marketing: v })}
                      testid="consent-marketing"
                      label="(Optional) Send me product and event updates" />
            </div>
          </div>

          <button
            type="button"
            onClick={submit}
            disabled={submitting || !consentsOk}
            className="w-full px-5 py-3 rounded-md bg-[#D4AF37] hover:bg-[#B5952F] text-[#0A192F] font-bold text-base disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="checkout-proceed"
          >
            <ShieldCheck className="h-4 w-4" />
            {planIsFree ? "Activate free plan" : "Proceed to secure payment"}
            <ChevronRight className="h-4 w-4" />
          </button>
          <p className="text-[11px] text-slate-500 flex items-start gap-2">
            <Info className="h-3 w-3 mt-0.5" />
            Diplomexa does not store your card details. Payments are processed
            securely via {isInr ? "Razorpay" : "Stripe"}. Final billing eligibility
            is determined by our server-side billing engine.
          </p>
        </main>
      </div>
    </DashboardLayout>
  );
}

function Row({ label, value, tone }) {
  return (
    <div className={`flex items-center justify-between ${tone === "success" ? "text-emerald-600" : tone === "muted" ? "text-slate-400" : ""}`}>
      <span className="text-slate-600">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

function PolicyLinks() {
  return (
    <div className="mt-5 pt-5 border-t border-slate-100 grid grid-cols-2 gap-1 text-[11px]">
      {[
        { to: "/terms", label: "Terms" },
        { to: "/privacy", label: "Privacy" },
        { to: "/refund-policy", label: "Refund Policy" },
        { to: "/billing-notice", label: "Billing Notice" },
      ].map((l) => (
        <Link key={l.to} to={l.to} target="_blank" className="text-[#0F4C81] hover:underline">
          {l.label}
        </Link>
      ))}
    </div>
  );
}

function Field({ label, value, onChange, hint, as = "input", options, colSpan }) {
  return (
    <label className={`flex flex-col text-xs ${colSpan === 2 ? "sm:col-span-2" : ""}`}>
      <span className="text-slate-500 mb-1">{label}</span>
      {as === "select" ? (
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="px-2 py-2 text-sm rounded-md border border-slate-200 focus:border-[#0F4C81] focus:ring-1 focus:ring-[#0F4C81] outline-none bg-white"
        >
          {(options || []).map((opt) => {
            if (typeof opt === "string") return <option key={opt} value={opt}>{opt}</option>;
            return <option key={opt.value} value={opt.value}>{opt.label}</option>;
          })}
        </select>
      ) : (
        <input
          type="text" value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="px-2 py-2 text-sm rounded-md border border-slate-200 focus:border-[#0F4C81] focus:ring-1 focus:ring-[#0F4C81] outline-none"
        />
      )}
      {hint && <span className="text-[10px] text-slate-400 mt-1">{hint}</span>}
    </label>
  );
}

function Check2({ v, onChange, label, testid }) {
  return (
    <label className="flex items-start gap-2 text-xs text-slate-700">
      <input type="checkbox" checked={!!v} onChange={(e) => onChange(e.target.checked)}
             className="mt-0.5" data-testid={testid} />
      <span>{label}</span>
    </label>
  );
}

// Razorpay Checkout.js loader (called only when needed)
let _rzpLoading = null;
function loadRazorpayScript() {
  if (window.Razorpay) return Promise.resolve();
  if (_rzpLoading) return _rzpLoading;
  _rzpLoading = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
  return _rzpLoading;
}
